/* eslint-disable @typescript-eslint/no-explicit-any */
import { EventEmitter } from './helpers/EventEmitter'; // TODO: Change this
import { ResourceTypeMap } from './constants/resources';
import { resolveKey } from './helpers/resolveKey';

type DataStoreTypesByName = {
  app: ResourceTypeMap;
};

type DataStoreInstancesByName = {
  [K in keyof DataStoreTypesByName]: DataStore<DataStoreTypesByName[K]>;
};

export type DataStoreInstance<
  K extends keyof DataStoreInstancesByName
> = DataStoreInstancesByName[K];

const instances: Partial<DataStoreInstancesByName> = {};

type Listener = (...args: Array<unknown>) => unknown;

type ChangeListener<T> = (newValue: T | null | undefined) => void;

type ExtractArgs<T extends string> = string extends T
  ? []
  : // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends `${infer Start}:${infer Param}/${infer Rest}`
  ? [string, ...ExtractArgs<Rest>]
  : // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends `${infer Start}:${infer Param}`
  ? [string]
  : [];

type NormalKeys<T> = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  [K in keyof T]: K extends `${infer S}:${infer P}` ? never : K;
}[keyof T] &
  string;

type InterpolatedKeys<T> = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  [K in keyof T]: K extends `${infer S}:${infer P}` ? K : never;
}[keyof T];

class DataStore<TypeMap extends ObjectOf<any>> {
  _data: Partial<TypeMap>;
  _emitter: EventEmitter<Record<string, Array<any>>>;

  constructor() {
    this._data = {};
    this._emitter = new EventEmitter<Record<string, Array<any>>>();
  }

  set<K extends NormalKeys<TypeMap>>(
    key: K,
    value: TypeMap[K] | null | undefined,
    context?: ObjectOf<any> | null | undefined,
  ) {
    let oldValue = this._data[key];
    if (value !== oldValue) {
      this._data[key] = value ?? undefined;
      let newContext = { ...context, oldValue: oldValue };
      this._emitter.emit('change', key, value, newContext);
      this._emitter.emit('change:' + key, value, newContext);
    }
  }

  setX<K extends InterpolatedKeys<TypeMap>>(
    key: K,
    keyArgs: ExtractArgs<K>,
    value: TypeMap[K] | null | undefined,
  ) {
    let resolvedKey = resolveKey(String(key), keyArgs) as K;
    this.set(resolvedKey as any, value);
  }

  get<K extends NormalKeys<TypeMap>>(key: K): TypeMap[K] | undefined {
    return this._data[key];
  }

  getX<K extends InterpolatedKeys<TypeMap>>(key: K, keyArgs: ExtractArgs<K>) {
    let resolvedKey = resolveKey(String(key), keyArgs) as K;
    return this.get(resolvedKey as any);
  }

  clearAll() {
    let data = this._data;
    Object.keys(data).forEach((key) => {
      // this.set(key, undefined, {clearAll: true});
      delete data[key];
    });
  }

  // TODO: Rewrite with clearer types (no string interpolation)
  on(eventName: string, listener: Listener): Subscription {
    this._emitter.addListener(eventName, listener);
    return {
      unsubscribe: () => {
        this._emitter.removeListener(eventName, listener);
      },
    };
  }

  onChange<K extends NormalKeys<TypeMap>>(
    key: K,
    listener: ChangeListener<TypeMap[K]>,
  ): Subscription {
    return this.on(`change:${key}`, listener as any);
  }

  static getInstance<K extends keyof DataStoreTypesByName>(
    name: K,
  ): DataStoreInstance<K> {
    let instance = instances[name];
    if (instance) {
      return instance as any;
    } else {
      return (instances[name] = new DataStore());
    }
  }
}

export default DataStore;
